import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, UserPlus, Mail, ShieldCheck, X, Search } from "lucide-react";

const input = "w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]";

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this host account?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Host removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const updateSender = async (id, patch) => {
    try {
      await api.put(`/admin/users/${id}`, patch);
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  if (user && user.role !== "admin")
    return <div className="p-8 text-[#4B5563]">Only admins can manage host accounts.</div>;

  return (
    <div className="p-8" data-testid="team-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Hosts</h1>
          <p className="text-[#4B5563] mt-1">Give hosts their own login to create experiences and campaigns. You control their sender email; they never see it.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] px-5 py-2.5 font-semibold flex items-center gap-2" data-testid="add-host-account-btn">
          <UserPlus className="w-4 h-4" /> Add host
        </button>
      </div>

      {users === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" data-testid="host-search-input" className="w-full border border-[#0A0A0A] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
            </div>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="host-role-filter" className="border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]">
              <option value="all">All roles</option>
              <option value="host">Hosts</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          {(() => {
            const s = q.trim().toLowerCase();
            const filtered = users.filter((u) => {
              if (roleFilter !== "all" && u.role !== roleFilter) return false;
              if (!s) return true;
              return `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(s);
            });
            if (filtered.length === 0)
              return <div className="bg-white border border-[#0A0A0A] p-10 text-center text-[#9CA3AF]" data-testid="host-list-empty">No hosts match your filters.</div>;
            return (
        <div className="bg-white border border-[#0A0A0A]">
          {filtered.map((u, i) => (
            <div key={u.id} className={`p-4 ${i > 0 ? "border-t border-[#E5E7EB]" : ""}`} data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#F1EEFB] flex items-center justify-center font-bold text-[#6D28B5]">{(u.name || u.email)[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">{u.name} {u.role === "admin" && <span className="text-xs bg-[#6D28B5] text-white px-2 py-0.5 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin</span>}</div>
                  <div className="text-xs text-[#4B5563]">{u.email}</div>
                </div>
                {u.role !== "admin" && (
                  <button onClick={() => remove(u.id)} className="p-2 hover:bg-red-50" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-[#FF3B30]" /></button>
                )}
              </div>
              {u.role !== "admin" && (
                <div className="mt-3 pl-14 grid grid-cols-2 gap-3 max-w-xl">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] flex items-center gap-1"><Mail className="w-3 h-3" /> Their sender email (hidden from host)</label>
                    <input defaultValue={u.sender_email || ""} onBlur={(e) => e.target.value !== (u.sender_email || "") && updateSender(u.id, { sender_email: e.target.value })} placeholder="host@yourdomain.com" className={input} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Sender name</label>
                    <input defaultValue={u.sender_name || ""} onBlur={(e) => e.target.value !== (u.sender_name || "") && updateSender(u.id, { sender_name: e.target.value })} placeholder="Host name" className={input} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
            );
          })()}
        </>
      )}

      {showCreate && <CreateHostModal onClose={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateHostModal({ onClose }) {
  const [f, setF] = useState({ name: "", email: "", password: "", sender_email: "", sender_name: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", f);
      toast.success("Host account created");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-host-modal">
      <div className="bg-white w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="font-heading font-black text-xl tracking-tighter">Add a host</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F3F4F6]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Host name" className={input} data-testid="host-name-input" />
          <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Login email" className={input} data-testid="host-email-input" />
          <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="Temporary password" className={input} data-testid="host-password-input" />
          <div className="border-t border-[#E5E7EB] pt-4">
            <div className="text-xs uppercase tracking-wider font-bold text-[#6D28B5] mb-2">Sender (hidden from host)</div>
            <div className="grid grid-cols-2 gap-3">
              <input value={f.sender_email} onChange={(e) => set("sender_email", e.target.value)} placeholder="sender email" className={input} />
              <input value={f.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="sender name" className={input} />
            </div>
          </div>
          <button onClick={create} disabled={saving || !f.email || !f.password} className="w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-6 py-3 font-semibold flex items-center justify-center gap-2" data-testid="save-host-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create host
          </button>
        </div>
      </div>
    </div>
  );
}
